import React, { useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Select,
  Table,
  Tag,
  Drawer,
  Form,
  Input,
  InputNumber,
  Switch,
  Statistic,
  message,
} from 'antd';
import {
  DollarOutlined,
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  CheckOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

const WhiteLabelPricing = () => {
  const [currency, setCurrency] = useState('USD');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const plans = useMemo(() => ([
    { key: 'starter', name: 'Starter', monthly: 99, cpmMarkup: 0.15, active: true, features: ['Basic reports', 'Email support'], clients: 12 },
    { key: 'pro', name: 'Pro', monthly: 299, cpmMarkup: 0.25, active: true, features: ['Advanced analytics', 'Priority support', 'Reports'], clients: 20 },
    { key: 'enterprise', name: 'Enterprise', monthly: 999, cpmMarkup: 0.35, active: false, features: ['SLA', 'Custom dashboards', 'SSO'], clients: 3 },
  ]), []);

  const kpis = useMemo(() => {
    const total = plans.length;
    const active = plans.filter(p => p.active).length;
    const avgMargin = Math.round(plans.reduce((s, p) => s + p.cpmMarkup, 0) / plans.length * 100);
    const totalClients = plans.reduce((s, p) => s + (p.clients || 0), 0);
    return { total, active, avgMargin, totalClients };
  }, [plans]);

  const openDrawer = (record) => {
    setSelected(record || null);
    form.setFieldsValue(record || { name: '', monthly: 0, cpmMarkup: 0.2, active: true, features: '' });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      // TODO: POST / PUT to backend
      void values;
      message.success(selected ? 'Plan updated' : 'Plan created');
      setDrawerOpen(false);
    } catch (e) {
      if (e?.errorFields) return;
      message.error('Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Plan', dataIndex: 'name', key: 'name' },
    { title: 'Monthly', dataIndex: 'monthly', key: 'monthly', width: 140, render: (v) => (
      <span>{currency} {v}</span>
    ) },
    { title: 'CPM Markup', dataIndex: 'cpmMarkup', key: 'cpmMarkup', width: 140, render: (v) => (
      <Tag color={v >= 0.3 ? 'red' : v >= 0.2 ? 'orange' : 'green'}>{Math.round(v * 100)}%</Tag>
    ) },
    { title: 'Clients', dataIndex: 'clients', key: 'clients', width: 120 },
    { title: 'Status', dataIndex: 'active', key: 'active', width: 120, render: (a) => (
      <Tag color={a ? 'green' : 'default'}>{a ? 'Active' : 'Disabled'}</Tag>
    ) },
    { title: 'Actions', key: 'actions', width: 220, render: (_, r) => (
      <Space>
        <Button icon={<EditOutlined />} onClick={() => openDrawer(r)}>Edit</Button>
        {r.active ? (
          <Button danger icon={<StopOutlined />}>Disable</Button>
        ) : (
          <Button type="primary" icon={<CheckOutlined />}>Enable</Button>
        )}
      </Space>
    )},
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>WhiteLabel Pricing Controls</Title>
          <Text type="secondary">Set client pricing, margins, and plan configurations.</Text>
        </div>

        {/* KPIs */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Space>
                <DollarOutlined />
                <Statistic title="Plans" value={kpis.total} />
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Space>
                <DollarOutlined />
                <Statistic title="Active" value={kpis.active} />
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Space>
                <DollarOutlined />
                <Statistic title="Avg Markup" value={kpis.avgMargin} suffix="%" />
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Space>
                <DollarOutlined />
                <Statistic title="Clients on Plans" value={kpis.totalClients} />
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Toolbar */}
        <Card bodyStyle={{ padding: 12 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={8}>
              <Space>
                <Text type="secondary">Currency</Text>
                <Select value={currency} onChange={setCurrency} style={{ width: 140 }}>
                  <Select.Option value="USD">USD</Select.Option>
                  <Select.Option value="EUR">EUR</Select.Option>
                  <Select.Option value="GBP">GBP</Select.Option>
                </Select>
              </Space>
            </Col>
            <Col xs={24} md={16} style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer(null)}>New Plan</Button>
            </Col>
          </Row>
        </Card>

        {/* Plans table */}
        <Card title="Plans" bodyStyle={{ padding: 0 }}>
          <Table rowKey="key" columns={columns} dataSource={plans} pagination={{ pageSize: 8 }} />
        </Card>

        {/* Create/Edit Drawer */}
        <Drawer
          title={selected ? `Edit Plan: ${selected.name}` : 'Create Plan'}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={520}
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            <Form.Item label="Plan Name" name="name" rules={[{ required: true, message: 'Enter plan name' }]}>
              <Input placeholder="Pro" />
            </Form.Item>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item label="Monthly Price" name="monthly" rules={[{ required: true, message: 'Enter monthly price' }]}>
                  <InputNumber min={0} style={{ width: '100%' }} addonBefore={currency} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="CPM Markup (%)" name="cpmMarkup" rules={[{ required: true, message: 'Enter CPM markup' }]}>
                  <InputNumber min={0} max={100} style={{ width: '100%' }} formatter={(v) => `${v}%`} parser={(v) => (v || '').replace('%','')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Features (comma separated)" name="features">
              <Input placeholder="Advanced analytics, Priority support" />
            </Form.Item>
            <Form.Item label="Active" name="active" valuePropName="checked">
              <Switch />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
                <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
              </Space>
            </div>
          </Form>
        </Drawer>
      </Space>
    </div>
  );
};

export default WhiteLabelPricing;
